<template>
    <v-dialog
        v-model="dialog"
        :fullscreen="!!isMobile"
        width="40%"
        max-width="1000"
        persistant
        :scrim="false"
        @click:outside="clearInput"
    >
        <template v-slot:activator="{props}">
            <v-btn color="primary" class="cursor-pointer consultant-btn" v-bind="props">
                <v-icon start>mdi-chat-outline</v-icon>
                <span>
                    {{ $t('virtual_consultant') }}
                </span>
            </v-btn>
        </template>
        <v-card>
            <v-card-actions class="justify-end px-5 pt-5 pb-0">
                <div class="text-h4 text-primary ma-auto">{{ $t('chat') }}</div>
                <v-icon class="v-card-close" @click="dialog = false">mdi-close</v-icon>
            </v-card-actions>
            <div class="wrapper pa-5">
                <div ref="messageWindow" class="message-box ma-1 pa-1 rounded bg-grey-lighten-4">
                    <v-card
                        v-for="(message, index) in messages"
                        :key="index"
                        :class="{'text-right': message.role === 'user'}"
                        class="mt-1 rounded-lg"
                    >
                        <v-list-item lines="two">
                            <v-list-item-title>
                                <v-icon size="small">mdi-account</v-icon>
                                <span :class="{'text-primary': message.role === 'user'}">
                                    {{ message.role }}
                                </span>
                                {{ getTime(message.created_at) }}
                            </v-list-item-title>
                            <v-list-item-subtitle class="ml-5 make-new-line" v-html="message.content[0].text.value" />
                        </v-list-item>
                    </v-card>
                </div>
                <div class="mt-2 d-flex text-no-wrap align-end">
                    <div class="textarea mr-5">
                        <v-textarea
                            v-model="message"
                            :placeholder="$t('enter_message')"
                            :label="$t('enter_message')"
                            :loading="isLoading"
                            :disabled="isLoading"
                            rows="2"
                            hide-details="auto"
                            variant="outlined"
                        />
                    </div>
                    <div class="d-flex flex-column justify-center align-center button-send">
                        <v-btn
                            variant="flat"
                            block
                            :loading="isLoading"
                            :disabled="!message.length && isLoading"
                            size="large"
                            color="primary"
                            @click="sendMessage"
                        >
                            {{ $t('send') }}
                        </v-btn>
                    </div>
                </div>
            </div>
        </v-card>
    </v-dialog>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator'

    @Component
    export class AssistantChat extends Vue {
        @Prop() page
        @Prop() instructions
        @Prop() searchKey
        @Prop() searchRequest
        isLoading = false
        messages = []
        message = ''
        messagesData = null
        thread = null
        run = null
        files = []
        assistant = null
        dialog = false

        mounted() {
            window.addEventListener('beforeunload', async () => {
                await this.clearAll()
            })
        }

        async beforeUnmount() {
            await this.clearAll()
        }

        async createTread() {
            try {
                this.thread = await this.$api.threads.post()
            } catch (e) {
                this.thread = null
            }
        }

        async createFile() {
            try {
                const res = await this.$api.files.post({
                    page: this.page,
                    searchKey: this.searchKey,
                    searchRequest: this.searchRequest,
                })
                this.files.push(res)
                // eslint-disable-next-line no-empty
            } catch (e) {}
        }

        async deleteFile(id) {
            try {
                await this.$api.files.remove({fileId: id})
                // eslint-disable-next-line no-empty
            } catch (e) {}
        }

        async createAssistant() {
            try {
                this.assistant = await this.$api.assistants.post({instructions: this.instructions, name: this.page})
                // eslint-disable-next-line no-empty
            } catch (e) {}
        }

        async deleteAssistant() {
            try {
                this.assistant = await this.$api.assistants.remove({assistantId: this.assistant.id})
                // eslint-disable-next-line no-empty
            } catch (e) {}
        }

        async createAssistantFile(fileId) {
            try {
                await this.$api.assistantsFiles.post({assistantId: this.assistant.id, fileId})
                // eslint-disable-next-line no-empty
            } catch (e) {}
        }

        async addMessage() {
            try {
                const message = {role: 'user', content: this.message}
                await this.$api.threadsMessages.post({threadId: this.thread.id, message})
                // eslint-disable-next-line no-empty
            } catch (e) {}
        }

        async getMessages() {
            try {
                this.messagesData = await this.$api.threadsMessages.get({threadId: this.thread.id})
                this.messages = this.messagesData.data.reverse()

                this.clearInput()
                this.isLoading = false
                // eslint-disable-next-line no-empty
            } catch (e) {}
        }

        async runTread() {
            try {
                this.run = await this.$api.threadsRuns.post({
                    threadId: this.thread.id,
                    assistantId: this.assistant.id,
                })
                // eslint-disable-next-line no-empty
            } catch (e) {}
        }

        async checkRunStatus() {
            try {
                this.run = await this.$api.threadsRuns.get({threadId: this.thread.id, runId: this.run.id})
                // eslint-disable-next-line no-empty
            } catch (e) {}
        }

        async sendMessage() {
            this.isLoading = true
            if (!this.assistant?.deleted) await this.createAssistant()
            if (!this.thread) await this.createTread()
            if (!this.files.length) {
                await this.createFile()
                await this.createAssistantFile(this.files[0].id)
                //     await (() => {
                //         this.files.map(async file => await this.createAssistantFile(file.id))
                //     })()
            }
            await this.addMessage()
            await this.runTread()
            await this.checkRunStatus()
            let checkStatus = setInterval(async () => {
                if (this.run.status === 'in_progress' || this.run.status === 'queued') {
                    await this.checkRunStatus()
                } else if (this.run.status === 'failed') {
                    clearInterval(checkStatus)
                    this.messages.push({
                        role: 'system',
                        content: [{text: {value: 'Oops smith went wrong ... Please try to refresh page'}}],
                    })
                    if (this.run?.last_error?.message) {
                        this.messages.push({
                            role: 'system',
                            content: [{text: {value: this.run.last_error.message}}],
                        })
                    }
                    this.isLoading = false
                } else {
                    clearInterval(checkStatus)
                    await this.getMessages()
                }
            }, 1400)
        }

        async clearAll() {
            this.files.map(async ({id}) => await this.deleteFile(id))
            this.files = []
            await this.deleteAssistant()
        }

        clearInput() {
            this.message = ''
        }

        getTime(val) {
            const date = new Date(val * 1000)
            const hours = date.getHours()
            const minutes = date.getMinutes()

            return `${hours}:${minutes}`
        }

        get personId() {
            return authStore.person.personId
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }
    }

    export default AssistantChat
</script>

<style lang="scss" scoped>
    @use 'vuetify/settings';
    @use 'sass:map';

    .consultant-btn {
        position: fixed;
        bottom: 40px;
        right: 30px;
        z-index: 1010;
    }

    .message-box {
        min-height: 100px;
        max-height: 400px;
        overflow-y: auto;
    }

    @media #{map.get(settings.$display-breakpoints, 'sm-and-down')} {
        .wrapper {
            display: flex;
            flex-direction: column;
            min-height: 90vh;
        }

        .message-box {
            min-height: 75vh;
            max-height: 75vh;
        }
    }

    .make-new-line {
        white-space: pre-line;
    }

    .textarea {
        flex: 0.8 1 auto;
    }

    .button-send {
        flex: 0.2 1 auto;
    }

    :deep(.v-btn--fixed.v-btn--bottom) {
        bottom: 48px !important;
    }
</style>
